import axios from 'axios';

import {
  User
} from '../model/user';

import {
  createHeader,
  handleResponseData,
  handleResponseError
} from '../utils/rest';
import dayjs from '../config/dayjs';

import { unshortenBase16 } from 'short-base16';
import { Store, Transaction } from '../model/transaction';
import Papa from 'papaparse';

const SERVER = `https://${process.env.REACT_APP_HOST}/API`;


export const getMe = async (email: String): Promise<User> => {
  if (email === '') {
    throw new Error('Email is required');
  }
  const headers = await createHeader(true, false);
  try {
    const response = await axios.post(
      `${SERVER}/Select`,
      {
        "module": "member",
        "target": "Getallmember",
        "data": {
          "StartRow": 1,
          "EndRow": 1,
          "Email": email.trim()
        }
      },
      {
        headers: headers
      }
    );
    const users = handleResponseData(response, []);
    if (!users.length) {
      throw new Error('User not found');
    }
    const me = users[0];
    const responseProfile = await axios.post(
      `${SERVER}/Select`,
      {
        "module": "member",
        "target": "GetMemberProfile",
        "data": {
          "MemberID": me.MemberID.trim()
        }
      },
      {
        headers: headers
      }
    );
    const item = handleResponseData(responseProfile);
    try {
      const user = {
        memberID: item.MemberID || null,
        idCard: item.IDCard || null,
        firstName: item.Firstname?.trim() || null,
        lastName: item.Lastname?.trim() || null,
        email: item.Email && item.Email !== 'EmailAddres' ? item.Email.trim().replace(/X\d{8}$/, '').toLowerCase() : null,
        mobilePhone: item.MobilePhone && item.MobilePhone !== '000000000000' ? item.MobilePhone.trim().replace(/X\d{8}$/, '') : null,
        currentPoint: item.CurrentPoint,
        level: item.Level || null,
        levelDateTime: item.LevelDateTime ? dayjs(item.LevelDateTime, 'DD-MM-YYYY HH:mm:ss') : null,
        levelExpiryDate: item.LevelExpiryDate ? dayjs(item.LevelExpiryDate, 'DD-MM-YYYY HH:mm:ss') : null,
        lastPurchaseDate: item.LastPurchaseDate ? dayjs(item.LastPurchaseDate, 'DD-MM-YYYY HH:mm:ss') : null,
        nextLevel: item.NextLevel || null,
        nextLevelPoint: item.NextLevelPoint,
        birthDate: item.BirthDate ? dayjs(item.BirthDate, 'DD-MM-YYYY') : null,
        salutation: item.Salutation || null,
        gender: item.Gender || null,
        nationality: item.Nationality || null,
        address1: item.Address1 || null,
        address2: item.Address2 || null,
        subDistrict: item.SubDistrict || null,
        district: item.District || null,
        province: item.Province || null,
        zipcode: item.Zipcode || null,
        country: item.Country || null,
        registerDate: item.RegisterDate ? dayjs(item.RegisterDate, 'DD-MM-YYYY HH:mm:ss') : null,
        lastUpdate: item.LastUpdate ? dayjs(item.LastUpdate, 'DD-MM-YYYY HH:mm:ss') : null,
        language: item.Language || null,
        subscription: item.Subscription || null,
        profilePictureURL: item.ProfilePictureURL || null,
        lineId: item.LINEID
          ? `U${unshortenBase16(item.LINEID.substring(4))}`
          : null,
        facebookEmail: item.FacebookEmail || null,
        googleEmail: item.GoogleEmail || null,
        sports: item.Sports && item.Sports.length
          ? item.Sports
            .filter((sport: any) => sport['SportsName'])
            ?.map((sport: any) => sport['SportsName'] ?? '') ?? []
          : [],
        otherSports: item.OtherSports || null,
        interest: item.Interest && item.Interest.length
          ? item.Interest
            .filter((sport: any) => sport['InterestName'])
            ?.map((interest: any) => interest['InterestName'] ?? '') ?? []
          : [],
        otherInterest: item.otherInterest || null,
        accountID: item.AccountID,
        rowNumber: item.RowNumber,
        memberlevel: item.Memberlevel || null,
        transactions: [],
        updated: false,
        updatedDuration: null
      } as User;
      const responseTransactions = await axios.post(
        `${SERVER}/Select`,
        {
          "module": "SalesOrder",
          "target": "GetTransaction",
          "data": {
            "MemberID": me.MemberID.trim()
          }
        },
        {
          headers: headers
        }
      );
      let storeName = '';
      const stores = await loadStores();
      const dataTransactions = handleResponseData(responseTransactions, []);
      user.transactions = dataTransactions.map((item: any) => {
        if (stores.length) {
          const store: Store | undefined = stores.find(
            store => store.shippingNumber === item.ShipNo
          );
          if (store) {
            if (store.storeType) {
              if (store.storeType !== 'Boutique') {
                storeName = `${store.storeType} ${store.branchName}`;
              } else {
                storeName = `${store.branchName}`;
              }
            } else {
              storeName = `${store.branchName}`;
            }
          }
        }
        return {
          transactionId: item.TransactionID,
          memberId: item.MemberID,
          crmSalesOrderId: item.CrmSalesOrderID,
          referenceTransactionId: item.ReferenceTransactionID,
          dateTime: item.TransactionDateTime
            ? dayjs(item.TransactionDateTime, 'DD-MM-YYYY HH:mm:ss')
            : null,
          shippingNumber: item.ShipNo,
          counterId: item.CounterID,
          totalSalesOrder: item.TotalSalesOrder,
          shippingFee: item.ShippingFee,
          point: item.Point,
          specialPoint: item.SpecialPoint,
          totalPoint: item.TotalPoint,
          source: item.Source,
          status: item.Status,
          rowNumber: item.RowNumber,
          storeName: storeName
        } as Transaction;
      });
      return user;
    } catch (error) {
      throw handleResponseError(error);
    }
  } catch (error) {
    throw handleResponseError(error);
  }
}

export const loadStores = async (): Promise<any[]> => {
  return new Promise((resolve, reject) => {
    fetch('/stores.csv')
      .then(response => response.text())
      .then(csvText => {
        Papa.parse(csvText, {
          header: true,
          complete: (results) => {
            const data = results.data;
            const stores = data.map((store: any) => {
              const branchNameParts = store['Branch Name'].split('_')[0];
              const branchName = branchNameParts.split(' ').map((word: string) => word.charAt(0).toUpperCase() + word.toLowerCase().slice(1)).join(' ');
              let storeType: string | undefined = undefined;
              if (store['Store Type'] !== 'NON-GROUP') {
                const storeNameParts = store['Store Type'].split('_')[0];
                storeType = storeNameParts.split(' ').map((word: string) => word.charAt(0).toUpperCase() + word.toLowerCase().slice(1)).join(' ');
              }
              return {
                bthCode: store['BTH CODE'],
                branchName: branchName,
                collectPoint: store['Collect Point'],
                corner: store['Corner'],
                getReward: store['Get Reward'],
                iicCornerCode: store['IIC Corner Code'],
                iicCornerCode1: store['IIC Corner Code_1'],
                shippingNumber: store['Shipno'],
                status: store['Status'],
                storeType: storeType
              } as Store;
            });
            resolve(stores);
          },
          error: (error: any) => {
            reject(error);
          }
        });
      })
      .catch(error => {
        reject(error);
      });
  });
};